/**
 * Plays a product's video while its card is hovered, then restores the image.
 *
 * Listeners are bound to the surrounding `.card-gallery` rather than to this
 * element so the whole image area is the hover target, and so the video can sit
 * behind the gallery link without swallowing clicks.
 */
class CardHoverVideo extends HTMLElement {
  /** @type {HTMLElement | null} */
  #gallery = null;

  /** @type {HTMLVideoElement | null} */
  #video = null;

  connectedCallback() {
    this.#video = this.querySelector('video');
    this.#gallery = this.closest('.card-gallery');

    if (!this.#video || !this.#gallery) return;

    this.#gallery.addEventListener('pointerenter', this.#play);
    this.#gallery.addEventListener('pointerleave', this.#stop);
  }

  disconnectedCallback() {
    this.#gallery?.removeEventListener('pointerenter', this.#play);
    this.#gallery?.removeEventListener('pointerleave', this.#stop);
    this.#gallery = null;
    this.#video = null;
  }

  /** @param {PointerEvent} event */
  #play = (event) => {
    if (event.pointerType !== 'mouse') return;

    const video = this.#video;
    if (!video) return;

    this.dataset.active = '';

    // Rejected when the browser refuses playback (blocked autoplay, decode
    // failure); fall back to the still image rather than showing an empty box.
    video.play().catch(() => delete this.dataset.active);
  };

  /** @param {PointerEvent} event */
  #stop = (event) => {
    if (event.pointerType !== 'mouse') return;

    const video = this.#video;
    if (!video) return;

    delete this.dataset.active;
    video.pause();
    video.currentTime = 0;
  };
}

if (!customElements.get('card-hover-video')) {
  customElements.define('card-hover-video', CardHoverVideo);
}
